/** @file Opens a project: GET /projects/:id/doc into the doc store and resets per-project editor state. */
import type { Doc } from '@pose2d/format/types';
import type { ApiClient } from '../api/createApiClient';
import { autorigHint } from '../panels/viewport/autorigHint';
import { openingFit } from '../panels/viewport/openingFit';
import { viewportStore } from '../panels/viewport/viewportStore';
import type { ViewportNotice } from '../panels/viewport/viewportTypes';
import { docStore } from '../stores/docStore';
import { playheadStore } from '../stores/playheadStore';
import { selectionStore } from '../stores/selectionStore';

/** The status line a project opens with: the Auto-rig offer of a fresh import, else none; like the fit, it never fails the open. */
function openingNotice(doc: Doc): ViewportNotice | null {
  try { return autorigHint(doc); } catch { return null; }
}

/** Arms the opening camera fit (done at the viewport's first real size). Rejects with the server's Pose2DError (NOT_FOUND for a wrong id); a late answer for another project is dropped. */
export async function loadProject(client: Pick<ApiClient, 'getDoc'>, projectId: string): Promise<void> {
  docStore.setState({ projectId, slice: null });
  const { doc, rev } = await client.getDoc(projectId);
  if (docStore.getState().projectId !== projectId) return;
  selectionStore.setState(selectionStore.getInitialState(), true);
  playheadStore.setState(playheadStore.getInitialState(), true);
  // The editor opens on the whole skeleton, framed once the viewport knows its real size (not a guessed share of the window).
  openingFit.arm(doc);
  // The status line starts clean per project; a fresh import (images, no slots) opens on the Auto-rig offer (N5).
  viewportStore.setState({ notice: openingNotice(doc) });
  docStore.setState({ projectId, slice: { confirmed: doc, pending: [], rev } });
}
